import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { CloudUploadOutlined, LockOutlined } from "@mui/icons-material";
import {
  type DragEvent,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";

import FieldLabel from "components/common/FieldLabel";
import UserAvatar from "components/common/UserAvatar";
import { CALIBRATE_COLORS } from "components/hoc/Theme";
import FormFooter, {
  FOOTER_BUTTON_SX,
} from "components/userManagement/form/FormFooter";
import FormHeader from "components/userManagement/form/FormHeader";
import StepHeading from "components/userManagement/form/StepHeading";
import { pushNotification } from "features/notificationSlice";
import {
  type SecurePerson,
  useFinalizeSecureUploadMutation,
  useGetSecureProjectCollectionsQuery,
  useGetSecureProjectsQuery,
  useGetSecureRecipientsQuery,
  usePresignSecureUploadMutation,
} from "features/secureUploads/secureUploads";
import useResponsive from "hooks/useResponsive";
import { useViewAs } from "hooks/useViewAs";
import { formatApiError } from "utils/formatApiError";
import { runLimited } from "utils/runLimited";

import { ConfidentialTag } from "./ProjectAccessEditor";
import UploadDropZone from "./UploadDropZone";
import UploadFileList from "./UploadFileList";
import { putToStorage } from "./storagePut";
import {
  collectionHelper,
  destinationLine,
  emptyQueue,
  filesToSend,
  finalizeFiles,
  keepAllowed,
  locationLabel,
  notifyHelper,
  projectHelper,
  removedFromShare,
  shareHelper,
  uploadBlocker,
  uploadedMessage,
  uploadQueueReducer,
} from "./uploadModel";

/** The place a page fixes; what it leaves open the uploader picks. */
export interface UploadPlace {
  projectId?: number | null;
  projectName?: string | null;
  collectionId?: number | null;
  collectionName?: string | null;
}

interface SecureUploadDialogProps {
  open: boolean;
  onClose: () => void;
  clientId: number;
  clientName: string;
  /** Nothing: the client page - Individual Files unless a project is picked. A
   * project: its page - a collection may be picked. A collection: its page. */
  place?: UploadPlace;
}

const TRANSFERS_AT_ONCE = 3;
const CONFIDENTIAL_CHECKED_SX = {
  "&.Mui-checked": { color: CALIBRATE_COLORS.confidential },
} as const;

/** Upload files where the page is, or where the uploader picks, shared with people
 * who will see them there. */
export default function SecureUploadDialog({
  open,
  onClose,
  clientId,
  clientName,
  place,
}: SecureUploadDialogProps) {
  const dispatch = useDispatch();
  const { profile, readOnly } = useViewAs();
  const { isPhone } = useResponsive();
  const staff = profile?.user_type === "internal";
  const [queue, send] = useReducer(
    uploadQueueReducer<File>,
    emptyQueue<File>(),
  );
  const [projectChoice, setProjectChoice] = useState("");
  const [collectionChoice, setCollectionChoice] = useState("");
  const [confidential, setConfidential] = useState(false);
  const [sharedWith, setSharedWith] = useState<number[]>([]);
  const [shareNotice, setShareNotice] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(true);
  const busy = queue.phase !== "editing";
  // Transfers to storage can be stopped; a save that started cannot.
  const transfers = useRef<AbortController | null>(null);
  const titleId = useRef(`upload-title-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (!open) return;
    send({ type: "reset" });
    setProjectChoice("");
    setCollectionChoice("");
    setConfidential(false);
    setSharedWith([]);
    setShareNotice(null);
    setNote("");
    setNotify(true);
  }, [open]);

  // Leaving the page would drop files on their way to storage.
  useEffect(() => {
    if (!open || !busy) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [open, busy]);

  const pickProject = place?.projectId == null;
  const pickCollection = place?.collectionId == null;
  const { data: openProjects, isSuccess: projectsLoaded } =
    useGetSecureProjectsQuery(
      { client_id: clientId, status: "open", page_size: 200 },
      { skip: !open || !pickProject },
    );
  const projects = openProjects?.results ?? [];
  const projectId = pickProject
    ? Number(projectChoice) || null
    : (place?.projectId ?? null);
  const { data: collections = [] } = useGetSecureProjectCollectionsQuery(
    projectId ?? 0,
    { skip: !open || !pickCollection || !projectId },
  );
  const collectionId = pickCollection
    ? Number(collectionChoice) || null
    : (place?.collectionId ?? null);
  const location = {
    client_id: clientId,
    project_id: projectId,
    collection_id: collectionId,
  };
  const { data: peopleData, isFetching: loadingPeople } =
    useGetSecureRecipientsQuery({ ...location, confidential }, { skip: !open });
  const people = useMemo(() => peopleData ?? [], [peopleData]);
  // Names of everyone offered so far: once a narrower list leaves someone out,
  // it no longer knows their name.
  const offered = useRef(new Map<number, string>());
  useEffect(() => {
    peopleData?.forEach((person) =>
      offered.current.set(person.id, person.name),
    );
  }, [peopleData]);

  // A narrower list (Confidential, a project) takes out whoever it leaves out,
  // and says who.
  useEffect(() => {
    if (loadingPeople || !peopleData) return;
    setSharedWith((chosen) => {
      const kept = keepAllowed(chosen, peopleData);
      if (kept.length === chosen.length) return chosen;
      setShareNotice(
        removedFromShare(
          chosen
            .filter((id) => !kept.includes(id))
            .map((id) => offered.current.get(id) ?? "Someone"),
        ),
      );
      return kept;
    });
  }, [peopleData, loadingPeople]);

  const [presign] = usePresignSecureUploadMutation();
  const [finalize] = useFinalizeSecureUploadMutation();

  const chosenPeople = useMemo(
    () =>
      sharedWith
        .map((id) => people.find((person) => person.id === id))
        .filter((person): person is SecurePerson => Boolean(person)),
    [sharedWith, people],
  );
  const projectName = pickProject
    ? projects.find((p) => p.id === projectId)?.name
    : place?.projectName;
  const collectionName = pickCollection
    ? collections.find((c) => c.id === collectionId)?.name
    : place?.collectionName;
  const files = queue.items.length;
  const blocker = uploadBlocker(files, sharedWith);
  const noProjects = projectsLoaded && projects.length === 0;

  const upload = async () => {
    // The storage PUT goes around the API, so Client View is refused here too.
    if (blocker || busy || readOnly) return;
    const items = queue.items;
    const stop = new AbortController();
    transfers.current = stop;
    send({ type: "send" });
    const sent = new Map(
      items.filter((item) => item.sent).map((item) => [item.id, item.sent]),
    );
    await runLimited(filesToSend(items), TRANSFERS_AT_ONCE, async (item) => {
      if (stop.signal.aborted) return;
      send({ type: "start", id: item.id });
      try {
        const contentType = item.file.type || "application/octet-stream";
        // Signed right before its transfer: a link lives an hour.
        const presigned = await presign({
          ...location,
          file_name: item.file.name,
          content_type: contentType,
        }).unwrap();
        await putToStorage(
          presigned.upload_url,
          presigned.headers,
          item.file,
          (percent) => send({ type: "progress", id: item.id, percent }),
          stop.signal,
        );
        const stored = {
          s3_key: presigned.s3_key,
          file_name: presigned.file_name,
          content_type: contentType,
        };
        sent.set(item.id, stored);
        send({ type: "sent", id: item.id, sent: stored });
      } catch (error) {
        const data = (error as { data?: unknown })?.data;
        send({
          type: "failed",
          id: item.id,
          error: data
            ? formatApiError(data, "Not sent.")
            : "Could not be sent to storage.",
        });
      }
    });
    send({ type: "sendDone" });
    // Cancelled: nothing is saved, whatever reached storage in the meantime.
    if (stop.signal.aborted) return;
    const stored = finalizeFiles(
      items.map((item) => ({ sent: sent.get(item.id) ?? null })),
    );
    if (!stored) return;
    try {
      const saved = await finalize({
        ...location,
        files: stored,
        shared_with: sharedWith,
        is_confidential: confidential,
        message: note.trim(),
        is_silent: staff && !notify,
      }).unwrap();
      dispatch(
        pushNotification({
          message: uploadedMessage(
            saved.files.length,
            locationLabel(projectName, collectionName),
            saved.emails_sent,
          ),
          severity: "success",
        }),
      );
      send({ type: "reset" });
      onClose();
    } catch (error) {
      send({
        type: "saveFailed",
        error: formatApiError(
          (error as { data?: unknown })?.data,
          "The files could not be saved.",
        ),
      });
    }
  };

  const cancel = () => {
    if (queue.phase === "saving") return;
    transfers.current?.abort();
    onClose();
  };

  const sendingLabel =
    queue.phase === "saving"
      ? "Saving..."
      : `Uploading ${queue.items.filter((i) => i.status === "sent").length} of ${files}...`;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isPhone}
      aria-labelledby={titleId.current}
      PaperProps={{
        sx: { borderRadius: isPhone ? 0 : 2, overflow: "hidden" },
        // A drop that misses the box must not open the file in the tab.
        onDragOver: (event: DragEvent) => event.preventDefault(),
        onDrop: (event: DragEvent) => event.preventDefault(),
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box id={titleId.current} sx={{ display: "contents" }}>
          <FormHeader
            avatar={<CloudUploadOutlined fontSize="small" />}
            title="Upload New File"
            onClose={cancel}
            closeDisabled={queue.phase === "saving"}
          />
        </Box>
        <Box
          sx={{
            p: 3,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            // A grid or shrinking row would fold the scrolling file list to
            // nothing once the dialog is full.
            "& > *": { flexShrink: 0 },
          }}
        >
          <Box>
            <StepHeading
              title={`Upload to ${clientName}`}
              subtitle={destinationLine({
                files,
                projectName,
                collectionName,
                canPickProject: pickProject && !noProjects,
                canPickCollection: pickCollection && Boolean(projectId),
              })}
            />
            <UploadDropZone
              disabled={busy}
              onFiles={(picked, folders) =>
                send({ type: "add", files: picked, folders })
              }
            />
          </Box>
          {queue.notice ? (
            <Alert
              severity="warning"
              onClose={() => send({ type: "dismissNotice" })}
            >
              {queue.notice}
            </Alert>
          ) : null}
          <UploadFileList
            items={queue.items}
            locked={busy}
            onRemove={(id) => send({ type: "remove", id })}
          />

          <FormControlLabel
            sx={{ alignItems: "flex-start", mx: 0, gap: 1 }}
            control={
              <Checkbox
                checked={confidential}
                disabled={busy}
                onChange={(event) => setConfidential(event.target.checked)}
                sx={{ p: 0.25, ...CONFIDENTIAL_CHECKED_SX }}
              />
            }
            label={
              <Box>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: confidential ? CALIBRATE_COLORS.confidential : "",
                  }}
                >
                  <LockOutlined sx={{ fontSize: 16 }} aria-hidden />
                  Upload as Confidential
                </Typography>
                <Typography
                  variant="caption"
                  component="div"
                  color="text.secondary"
                >
                  Applies to all files in this upload. Client users without the
                  Confidential designation will not see these files.
                </Typography>
              </Box>
            }
          />

          {pickProject || pickCollection ? (
            <>
              <Divider />
              <Box sx={{ display: "grid", gap: 2 }}>
                {pickProject ? (
                  <Box>
                    <FieldLabel htmlFor="upload-project">Project</FieldLabel>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      id="upload-project"
                      inputProps={{ "aria-label": "Project" }}
                      value={projectChoice}
                      disabled={busy || noProjects}
                      helperText={projectHelper(staff, !noProjects)}
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (value) =>
                          value ? (
                            (projects.find((p) => String(p.id) === value)
                              ?.name ?? "")
                          ) : (
                            <Box
                              component="span"
                              sx={{ color: "text.disabled" }}
                            >
                              Select a project
                            </Box>
                          ),
                      }}
                      onChange={(event) => {
                        setProjectChoice(event.target.value);
                        setCollectionChoice("");
                      }}
                    >
                      <MenuItem value="">
                        No project (Individual Files)
                      </MenuItem>
                      {projects.map((project) => (
                        <MenuItem key={project.id} value={String(project.id)}>
                          {project.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                ) : null}
                {pickCollection && projectId ? (
                  <Box>
                    <FieldLabel htmlFor="upload-collection">
                      Collection
                    </FieldLabel>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      id="upload-collection"
                      inputProps={{ "aria-label": "Collection" }}
                      value={collectionChoice}
                      disabled={busy}
                      helperText={collectionHelper(staff, files)}
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (value) =>
                          value ? (
                            (collections.find((c) => String(c.id) === value)
                              ?.name ?? "")
                          ) : (
                            <Box
                              component="span"
                              sx={{ color: "text.disabled" }}
                            >
                              Select a collection
                            </Box>
                          ),
                      }}
                      onChange={(event) =>
                        setCollectionChoice(event.target.value)
                      }
                    >
                      <MenuItem value="">No collection</MenuItem>
                      {collections.map((collection) => (
                        <MenuItem
                          key={collection.id}
                          value={String(collection.id)}
                        >
                          {collection.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                ) : null}
              </Box>
            </>
          ) : null}

          <Divider />
          <Box sx={{ display: "grid", gap: 1.5 }}>
            <Box>
              <FieldLabel htmlFor="upload-share-with" required>
                Share with
              </FieldLabel>
              <Autocomplete<SecurePerson, true>
                multiple
                id="upload-share-with"
                options={people}
                value={chosenPeople}
                loading={loadingPeople}
                disabled={busy}
                onChange={(_event, value) => {
                  setShareNotice(null);
                  setSharedWith(value.map((person) => person.id));
                }}
                getOptionLabel={(person) => person.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderOption={(props, person) => (
                  <Box
                    component="li"
                    {...props}
                    key={person.id}
                    sx={{ gap: 1 }}
                  >
                    <UserAvatar person={person} size={24} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2">{person.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {person.organization}
                        {person.email ? ` - ${person.email}` : ""}
                      </Typography>
                    </Box>
                    {staff && person.confidential ? <ConfidentialTag /> : null}
                  </Box>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((person, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={person.id}
                      size="small"
                      avatar={<UserAvatar person={person} size={20} />}
                      label={person.name}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder={chosenPeople.length ? "" : "Add a recipient"}
                    helperText={shareHelper(staff)}
                  />
                )}
                noOptionsText={
                  confidential
                    ? "Nobody here holds Confidential Access"
                    : "Nobody else can see files here"
                }
              />
              {shareNotice ? (
                <Alert
                  severity="info"
                  sx={{ mt: 1 }}
                  onClose={() => setShareNotice(null)}
                >
                  {shareNotice}
                </Alert>
              ) : null}
            </Box>
            {staff ? (
              <FormControlLabel
                sx={{ alignItems: "flex-start", mx: 0, gap: 1 }}
                control={
                  <Checkbox
                    checked={notify}
                    disabled={busy}
                    onChange={(event) => setNotify(event.target.checked)}
                    sx={{ p: 0.25 }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Notify Recipients
                    </Typography>
                    <Typography
                      variant="caption"
                      component="div"
                      color="text.secondary"
                    >
                      {notifyHelper(notify)}
                    </Typography>
                  </Box>
                }
              />
            ) : null}
          </Box>

          <Divider />
          <Box>
            <FieldLabel htmlFor="upload-note">Note</FieldLabel>
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              id="upload-note"
              placeholder="Visible to anyone who can see the files. Included in the notification email."
              value={note}
              disabled={busy}
              onChange={(event) => setNote(event.target.value)}
            />
          </Box>

          {queue.failure ? (
            <Alert severity="error">{queue.failure}</Alert>
          ) : null}
        </Box>
      </DialogContent>
      <FormFooter
        mode="edit"
        isFirstStep
        onBack={() => undefined}
        onCancel={cancel}
        cancelDisabled={queue.phase === "saving"}
      >
        <Button
          variant="contained"
          size="small"
          onClick={upload}
          disabled={busy || Boolean(blocker) || readOnly}
          title={blocker ?? undefined}
          sx={FOOTER_BUTTON_SX}
        >
          {busy ? sendingLabel : "Upload"}
        </Button>
      </FormFooter>
    </Dialog>
  );
}
